// Translation management utilities: validation, completeness, debugging, tracking

const flattenKeys = (obj, prefix = '') => {
  const keys = [];
  if (!obj || typeof obj !== 'object') return keys;
  Object.keys(obj).forEach((k) => {
    const path = prefix ? `${prefix}.${k}` : k;
    if (obj[k] && typeof obj[k] === 'object' && !Array.isArray(obj[k])) {
      keys.push(...flattenKeys(obj[k], path));
    } else {
      keys.push(path);
    }
  });
  return keys;
};

export const diffKeys = (base, compare) => {
  const baseKeys = new Set(flattenKeys(base));
  const compareKeys = new Set(flattenKeys(compare));
  const missingInCompare = [];
  const extraInCompare = [];
  baseKeys.forEach((k) => { if (!compareKeys.has(k)) missingInCompare.push(k); });
  compareKeys.forEach((k) => { if (!baseKeys.has(k)) extraInCompare.push(k); });
  return { missingInCompare, extraInCompare };
};

export const completenessScore = (base, compare) => {
  const baseKeys = flattenKeys(base);
  const { missingInCompare } = diffKeys(base, compare);
  const total = baseKeys.length || 1;
  const complete = total - missingInCompare.length;
  const percentage = Math.round((complete / total) * 100);
  return { total, complete, missing: missingInCompare.length, percentage };
};

// Usage tracking (in-memory only)
const usage = {
  seen: new Map(), // key -> count
};

export const trackUsage = (key) => {
  const current = usage.seen.get(key) || 0;
  usage.seen.set(key, current + 1);
};

export const getUsage = () => {
  return Array.from(usage.seen.entries()).map(([key, count]) => ({ key, count }));
};

export const resetUsage = () => {
  usage.seen.clear();
};

// Integrity check: ensure all placeholders in base exist in compare
const extractPlaceholders = (value) => {
  if (typeof value !== 'string') return [];
  return Array.from(value.matchAll(/\{(\w+)\}/g)).map((m) => m[1]);
};

export const checkPlaceholdersIntegrity = (base, compare) => {
  const problems = [];
  const validateNode = (bNode, cNode, path = '') => {
    if (typeof bNode === 'string') {
      const required = new Set(extractPlaceholders(bNode));
      if (typeof cNode === 'string') {
        const have = new Set(extractPlaceholders(cNode));
        required.forEach((ph) => { if (!have.has(ph)) problems.push({ key: path, placeholder: ph }); });
      }
      return;
    }
    if (bNode && typeof bNode === 'object') {
      Object.keys(bNode).forEach((k) => {
        const nextPath = path ? `${path}.${k}` : k;
        validateNode(bNode[k], cNode ? cNode[k] : undefined, nextPath);
      });
    }
  };
  validateNode(base, compare);
  return problems;
};

// Development: warn about missing keys
export const warnMissingKeys = (base, compare, label = 'i18n') => {
  const { missingInCompare } = diffKeys(base, compare);
  if (missingInCompare.length > 0) {
    // eslint-disable-next-line no-console
    console.warn(`[${label}] Missing keys:`, missingInCompare);
  }
};

export const TranslationUtils = {
  diffKeys,
  completenessScore,
  trackUsage,
  getUsage,
  resetUsage,
  checkPlaceholdersIntegrity,
  warnMissingKeys,
};

export default TranslationUtils;


